// Testing connection - Ziad
// App.jsx
import React, { useEffect, useState } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import Search from "./pages/Search";
import JobDetail from "./pages/JobDetail";
import CandidateAccount from "./pages/CandidateAccount";
import RizkiraAutoHeight from './components/RizkiraAutoHeight';
import { persistEmbedToken, getEmbedTokenFromHash, autoGenerateToken } from "./services/api";

function useAccessParams() {
  const { search } = useLocation();
  const usp = new URLSearchParams(search);
  let orgId = usp.get("orgId");
  let token = usp.get("token");

  console.log('Router params from useLocation:', { search: search.substring(0, 50) + (search.length > 50 ? '...' : ''), orgId, token: token ? 'PRESENT' : 'NULL' });

  if (!orgId || !token) {
    const fullUsp = new URLSearchParams(window.location.search);
    orgId = fullUsp.get("orgId") || orgId;
    token = fullUsp.get("token") || token;
    console.log('Fallback search params:', { orgId, token: token ? 'PRESENT' : 'NULL' });
  }

  if (!token) {
    const hash = window.location.hash.substring(1);
    if (hash.startsWith('token=')) {
      token = decodeURIComponent(hash.substring(6));
      console.log('Token from hash:', token ? 'EXTRACTED (length: ' + token.length + ')' : 'FAILED');
      try { persistEmbedToken(token); } catch {}
    }
  }

  // Last-resort fallback to persisted token (if hash was lost during navigation)
  if (!token || token === 'null' || token === 'NULL') {
    const persisted = getEmbedTokenFromHash();
    if (persisted) token = persisted;
  }

  return { orgId, token };
}

function RequireAccess({ children }) {
  const { pathname } = useLocation();
  const { orgId, token } = useAccessParams();
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [generatedToken, setGeneratedToken] = useState(null);
  const [tokenError, setTokenError] = useState(null);

  // Public, read-only pages that should work without orgId/token
  const isPublicPath = pathname === "/" || /^\/job\/[^/]+$/i.test(pathname);

  console.log('RequireAccess final check:', { orgId, token: token ? 'PRESENT' : 'NULL', pathname, isPublicPath });

  // Auto-generate token if orgId is present but token is missing
  useEffect(() => {
    async function generateToken() {
      if (orgId && (!token || token === 'null' || token === 'NULL') && !isGeneratingToken && !generatedToken) {
        console.log('[RequireAccess] Auto-generating token for orgId:', orgId);
        setIsGeneratingToken(true);
        setTokenError(null);
        
        try {
          const newToken = await autoGenerateToken(orgId);
          if (newToken) {
            setGeneratedToken(newToken);
            console.log('[RequireAccess] Token generated, reloading...');
            // Update URL with the new token
            const url = new URL(window.location.href);
            url.searchParams.set('token', newToken);
            window.history.replaceState({}, '', url.toString());
            // Force a re-render by updating state
            window.location.reload();
          } else {
            setTokenError('Failed to generate authentication token');
          }
        } catch (error) {
          console.error('[RequireAccess] Token generation error:', error);
          setTokenError('Error generating authentication token');
        } finally {
          setIsGeneratingToken(false);
        }
      }
    }
    
    generateToken();
  }, [orgId, token, isGeneratingToken, generatedToken]);

  // Show loading state while generating token
  if (orgId && (!token || token === 'null' || token === 'NULL') && isGeneratingToken) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <h2>Initializing...</h2>
        <p>Generating authentication token for organization {orgId}...</p>
      </div>
    );
  }

  // Show error if token generation failed
  if (tokenError) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Authentication Error</h2>
        <p style={{ color: 'red' }}>{tokenError}</p>
        <p>Please check your organization ID and try again.</p>
      </div>
    );
  }

  if (!isPublicPath && (!orgId || !token)) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Access required</h2>
        <p>
          The careers page must be opened with a valid <code>orgId</code> parameter.
        </p>
        <p style={{ color: 'red' }}>
          DEBUG: orgId={orgId || 'MISSING'}, token={token ? 'PRESENT' : 'MISSING'}
        </p>
      </div>
    );
  }
  
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<RequireAccess><Search /></RequireAccess>} />
      <Route path="/job/:id" element={<RequireAccess><JobDetail /></RequireAccess>} />
      <Route path="/account" element={<CandidateAccount />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
